// The local-override rules (owner 2026-09-12, migration 1123) — pure, and in their own module
// so they can be tested without mounting a form. The composer draws them; the page sends what
// they produce; nothing else may restate them.
// ✍️ THE LOCAL OVERRIDE (owner 2026-09-12, migration 1123). "Keep the local override path open.
// Do not refuse it outright" — but an inherited value the agent changes must SAY it was changed,
// and say why, and the source it was changed against must be recorded, so that when the source
// moves again all three values can be put in front of a person.
//
// WHICH FIELDS. Only the ones that were INHERITED: a value another record supplied. A template
// default is not a source — it is the form filling a box for the agent, and demanding a sentence
// every time somebody changes 30 days to 14 would make the whole mechanism something to be got
// past rather than read. The derived rungs (period, lease_start) come from other answers on this
// same page and explain themselves. Everything else names a record somebody wrote.
const DERIVED_SOURCES = new Set(['none', 'default', 'period', 'lease_start', 'version'])
// ✏️ A NOTE IS CORRECTED, NOT OVERRIDDEN (owner 2026-09-22: "agents scribble names when filling
// meeting details… owners' names are inherited from portals where they never say their name, so I
// get this error"). A NAME typed on the meeting card or scraped off an advert, and the co-broke's
// person/company pick and computed share, are working notes — putting the real name in is fixing
// the note, and must not demand a reason. A name out of a CONTRACT is a record and still does.
const NOTE_KEYS = new Set(['tenant_name', 'cobroke_agent_name', 'cobroke_agency_name', 'cobroke_kind', 'cobroke_agent_id_kind', 'cobroke_share', 'cobroke_phone', 'cobroke_email', 'cobroke_active'])
const NOTE_SOURCES = new Set(['meeting', 'property', 'requirement', 'fill_form', 'listing', 'portal'])
export const isInherited = f => !!f && !DERIVED_SOURCES.has(String(f.source || 'none'))
  && !(NOTE_KEYS.has(f.key) && NOTE_SOURCES.has(String(f.source || '')))

// Two values of a field are the same ANSWER. Money is {value,currency}; a checklist is an array
// whose order is not part of the answer; everything else compares as text, so a number typed back
// over itself is not an edit.
export function sameAnswer(a, b) {
  if (a && b && typeof a === 'object' && typeof b === 'object' && 'value' in a && 'value' in b) {
    return String(a.value ?? '') === String(b.value ?? '') && (a.currency || '') === (b.currency || '')
  }
  if (Array.isArray(a) || Array.isArray(b)) return JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
  return String(a ?? '') === String(b ?? '')
}

// 🕳️ AN EMPTY SOURCE SUPPLIED NOTHING (live test 2026-09-23: importing an earlier lease seeded an EMPTY
// prepayment list, and filling that required field then demanded "why does it differ — the source says: —").
// Nothing was inherited, so there is nothing to override: no reason, no override record.
export const hasAnswer = v => !(v == null || v === '' || (Array.isArray(v) && v.length === 0)
  || (typeof v === 'object' && !Array.isArray(v) && 'value' in v && (v.value == null || v.value === '')))

// ONE test for "the agent moved an inherited value" — the reason box beside the field and the save bar both
// ask this, so they cannot disagree.
// 🔤 A SOURCE THE CONTRACT CANNOT PRINT IS CORRECTED, NOT OVERRIDDEN (owner 2026-09-24: "fix it and all pointless
// frictions"). A stored address in Russian („ул. С.Цинцадзе") is refused on a Georgian contract
// (address_not_georgian); retyping it in Georgian is the fix the form itself asks for, so it must not also
// demand "why does it differ from the source".
const hasCyrillic = v => [...String(v ?? '')].some(c => c.codePointAt(0) >= 0x400 && c.codePointAt(0) <= 0x4ff)
const UNPRINTABLE_SOURCE = { address: hasCyrillic }

export function movedFromSource(f, values, seeded) {
  return isInherited(f) && !!seeded && Object.prototype.hasOwnProperty.call(seeded, f.key)
    && hasAnswer(seeded[f.key]) && !sameAnswer(values?.[f.key], seeded[f.key])
    && !UNPRINTABLE_SOURCE[f.key]?.(seeded[f.key])
}

// Which inherited fields the agent has moved, and what each was moved AGAINST. The seed is the
// frozen copy of what every rung supplied, so this is a comparison and never a guess.
export function overriddenKeys(fields, values, seeded) {
  return (fields || []).filter(f => movedFromSource(f, values, seeded)).map(f => f.key)
}

// The payload: an entry per moved field carrying the reason and the source value it was decided
// against, plus an explicit null for a field that HAD an override and no longer diverges — the
// server clears that one rather than keeping the record of a decision that was undone.
// ⚠️ source_value is the SEEDED value, never the current one. It is what was on the screen.
export function overridesPayload(fields, values, seeded, reasons, existing) {
  const out = {}
  const now = new Set(overriddenKeys(fields, values, seeded))
  for (const k of now) out[k] = { value: values[k] ?? null, source_value: seeded[k] ?? null, reason: String(reasons?.[k] || '').trim() }
  for (const k of Object.keys(existing || {})) if (!now.has(k)) out[k] = null
  return out
}
